import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Save, Loader2, Lock, Trash2, Info, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { sessionKeyManager } from '../utils/encryption';
import AnalysisModal from '../components/AnalysisModal';
import { useSelahRest } from '../contexts/SelahRestContext';

// Detect the user's browser family so we can surface the right "how to
// re-enable microphone" instructions when access has been denied.
const detectBrowser = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/CriOS|Chrome\//i.test(ua) && !/Edg\//i.test(ua) && !/OPR\//i.test(ua)) return 'chrome';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'safari';
  return 'other';
};

const MIC_INSTRUCTIONS = {
  chrome: 'Click the lock icon (or camera icon) in the address bar → set Microphone to "Allow" → reload the page.',
  edge: 'Click the lock icon in the address bar → Permissions → set Microphone to "Allow" → reload the page.',
  firefox: 'Click the lock icon in the address bar → Connection Secure → More Information → Permissions → remove the "Blocked" state for Microphone → reload.',
  safari: 'Safari → Settings for This Website → set Microphone to "Allow" → reload the page.',
  other: 'Open your browser’s site settings for this page and allow Microphone, then reload.',
};

const Record = () => {
  const navigate = useNavigate();
  const { startRest } = useSelahRest();
  const [downloadText, setDownloadText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [savedDownload, setSavedDownload] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);

  const recognitionRef = useRef(null);
  const hasStartedRef = useRef(false);
  const browserFamily = React.useMemo(() => detectBrowser(), []);

  // Proactive permission check on mount so we can guide the user before
  // they even click the mic. Uses the Permissions API where available.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (!cancelled && status.state === 'denied') {
            setMicPermissionDenied(true);
          }
          if (status && !status.__selahBound) {
            status.__selahBound = true;
            status.onchange = () => {
              if (status.state === 'granted') setMicPermissionDenied(false);
              if (status.state === 'denied') setMicPermissionDenied(true);
            };
          }
        }
      } catch (_) {
        // Non-fatal — Safari doesn’t implement Permissions API for microphone.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Check if Web Speech API is supported
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setDownloadText((prev) => prev + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setMicPermissionDenied(true);
          toast.error('Microphone access was blocked', {
            description: 'See the guidance below to re-enable, or keep typing — text works fine too.',
          });
        } else if (event.error === 'no-speech') {
          toast.info('No speech detected — try speaking a little closer to the mic.');
        } else if (event.error === 'aborted') {
          // Normal user-initiated stop, no toast
        } else {
          toast.error('Voice capture stopped', { description: event.error });
        }
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        if (isRecording && hasStartedRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.error('Failed to restart recognition:', e);
            setIsRecording(false);
          }
        }
      };
    } else {
      setSpeechSupported(false);
    }

    return () => {
      if (recognitionRef.current && isRecording) {
        recognitionRef.current.stop();
      }
    };
  }, [isRecording]);

  const toggleRecording = async () => {
    if (!recognitionRef.current) {
      toast.error('Voice capture isn’t supported in this browser', {
        description: 'Please type your download, or try Chrome / Edge on desktop for hands-free mode.',
      });
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      hasStartedRef.current = false;
      setIsRecording(false);
      return;
    }

    // Explicitly request mic access before starting recognition. This gives us
    // a proper permission prompt in browsers where SpeechRecognition would
    // otherwise silently fail without asking.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately release the audio track — SpeechRecognition manages its own.
        stream.getTracks().forEach((t) => t.stop());
        setMicPermissionDenied(false);
      }
    } catch (err) {
      console.error('getUserMedia denied:', err);
      setMicPermissionDenied(true);
      toast.error('Microphone access was blocked', {
        description: 'See the guidance below the mic — or keep typing, text works fine too.',
      });
      return;
    }

    try {
      recognitionRef.current.start();
      hasStartedRef.current = true;
      setIsRecording(true);
      toast.success('Listening… speak whenever you’re ready.');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Couldn’t start voice capture', {
        description: error?.message || 'Please try again in a moment.',
      });
    }
  };

  const handleRetryMic = async () => {
    // User has fixed permissions in their browser and wants to try again.
    // We re-request via getUserMedia — if the browser still remembers a
    // "denied" decision, this returns rejected and we tell the user to reload.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        setMicPermissionDenied(false);
        toast.success('Microphone access restored.');
      } else {
        toast.info('Your browser doesn’t expose a permission API — try reloading the page.');
      }
    } catch (_) {
      toast.error('Still blocked', {
        description: 'Please follow the browser instructions below, then reload this page.',
      });
    }
  };

  const handleSave = async () => {
    if (!downloadText.trim()) {
      toast.error('Please enter or record some text first.');
      return;
    }

    setIsSaving(true);

    try {
      // Get encryption key
      const key = await sessionKeyManager.getOrCreateKey();
      const { encryption } = await import('../utils/encryption');
      
      // Encrypt the text
      const { ciphertext, iv } = await encryption.encrypt(downloadText, key);

      // Create download with analysis
      const result = await api.createDownload(ciphertext, iv, downloadText);

      // Trigger the Selah Rest ritual: 60 seconds of guided pause before
      // the user sees their analysis. When rest completes/is skipped, we
      // proceed to reveal the analysis (or navigate to History if none).
      startRest({
        onComplete: () => {
          if (result.analysis) {
            setAnalysis(result.analysis);
            setSavedDownload(result);
            setShowAnalysisModal(true);
          } else {
            toast.success('Download saved successfully!');
            setDownloadText('');
            navigate('/history');
          }
        },
      });
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save download. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setDownloadText('');
    toast.info('Text cleared');
  };

  const handleAnalysisClose = () => {
    setShowAnalysisModal(false);
    setDownloadText('');
    toast.success('Download saved successfully!');
    navigate('/history');
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="text-3xl sm:text-4xl font-semibold mb-3 text-[var(--sd-fg)]">
            Record a Download
          </h1>
          <p className="text-base text-[var(--sd-muted-fg)]">
            What's stirring in your heart today?
          </p>
        </motion.div>

        {/* Main recording card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card p-6 sm:p-8">
            {/* Privacy badge */}
            <div className="flex items-center justify-between mb-6">
              <div className="privacy-badge">
                <Lock size={14} />
                <span>Encrypted on device</span>
              </div>
              <div className="text-xs text-[var(--sd-muted-fg)]">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>

            {/* Textarea */}
            <Textarea
              value={downloadText}
              onChange={(e) => setDownloadText(e.target.value)}
              placeholder="Type your spiritual insight, or use the microphone below to speak..."
              className="min-h-[300px] text-base leading-relaxed resize-none mb-6"
              data-testid="record-textarea"
              disabled={isSaving}
            />

            {/* Character count */}
            <div className="text-xs text-[var(--sd-muted-fg)] mb-6 text-right">
              {downloadText.length} characters
            </div>

            {/* Recording dock */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Microphone button */}
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleRecording}
                  disabled={isSaving || micPermissionDenied}
                  data-testid="record-mic-button"
                  aria-label={isRecording ? 'Stop recording' : 'Start recording'}
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center
                    transition-all duration-200
                    mic-button
                    ${isRecording
                      ? 'recording bg-[var(--sd-primary)]'
                      : 'bg-[var(--sd-surface)] hover:bg-[var(--sd-surface-hover)] border border-[var(--sd-border)]'
                    }
                    ${(isSaving || micPermissionDenied) ? 'opacity-50 cursor-not-allowed' : ''}
                    focus-visible:ring-2 focus-visible:ring-[var(--sd-primary)]/40
                  `}
                >
                  {isRecording ? (
                    <MicOff size={24} className="text-[var(--sd-primary-fg)]" />
                  ) : (
                    <Mic size={24} className="text-[var(--sd-fg)]" />
                  )}
                </button>
                <div className="text-sm">
                  {isRecording ? (
                    <p className="text-[var(--sd-primary)] font-medium">Recording...</p>
                  ) : (
                    <p className="text-[var(--sd-muted-fg)]">
                      {micPermissionDenied
                        ? 'Mic access is blocked'
                        : !speechSupported
                          ? 'Voice not supported here'
                          : 'Click to record'}
                    </p>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleClear}
                  disabled={!downloadText || isSaving}
                  variant="ghost"
                  className="text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
                  data-testid="record-clear-button"
                >
                  <Trash2 size={16} className="mr-2" />
                  Clear
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!downloadText.trim() || isSaving}
                  data-testid="record-save-button"
                  className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save & Analyze
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Mic-denied recovery panel */}
            {micPermissionDenied && (
              <div
                className="mt-5 p-4 rounded-xl border border-[var(--sd-border)] bg-[var(--sd-surface)]"
                data-testid="mic-denied-panel"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-[var(--sd-primary)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--sd-fg)] mb-1">
                      Microphone access is blocked in this browser
                    </p>
                    <p className="text-xs text-[var(--sd-muted-fg)] leading-relaxed mb-3">
                      No worries — you can keep typing your download in the box
                      above and everything else (analysis, insights, prayer)
                      works exactly the same. To turn the mic back on:
                    </p>
                    <div className="text-xs text-[var(--sd-fg-secondary)] leading-relaxed p-3 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-bg)] mb-3">
                      {MIC_INSTRUCTIONS[browserFamily] || MIC_INSTRUCTIONS.other}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRetryMic}
                        className="border-[var(--sd-primary)]/50 text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10"
                        data-testid="mic-retry-button"
                      >
                        <RefreshCw size={13} className="mr-1.5" />
                        Try mic again
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.location.reload()}
                        className="text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
                        data-testid="mic-reload-button"
                      >
                        Reload page
                      </Button>
                      <span className="text-[11px] text-[var(--sd-muted-fg)] ml-auto">
                        Or set up Siri / IFTTT ambient capture in Settings
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Voice-not-supported (Safari older versions) */}
            {!speechSupported && !micPermissionDenied && (
              <div className="mt-5 flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] p-3 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)]">
                <Info size={14} className="text-[var(--sd-primary)] mt-0.5 flex-shrink-0" />
                <span>
                  In-browser voice capture isn&apos;t available here (Safari has limited
                  Web Speech support). Text works perfectly — or set up
                  <span className="text-[var(--sd-fg)] font-medium"> Siri Shortcut </span>
                  ambient capture from Settings for hands-free logging.
                </span>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center text-sm text-[var(--sd-muted-fg)]"
        >
          <p>
            Capture your spiritual "aha moments" as they happen.
            AI will help you explore deeper insights, relevant scripture, and a personalized prayer.
          </p>
        </motion.div>
      </div>

      {/* Analysis Modal */}
      <AnimatePresence>
        {showAnalysisModal && analysis && (
          <AnalysisModal
            analysis={analysis}
            onClose={handleAnalysisClose}
            downloadText={downloadText}
            download={savedDownload}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Record;